import HikingIcon from "@mui/icons-material/Hiking";
import BusinessIcon from "@mui/icons-material/Business";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import {
  AppBar,
  Box,
  Button,
  Container,
  Link as MuiLink,
  Skeleton,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import firebase from "firebase/app";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { useAuth, useSigninCheck, useUser } from "reactfire";
import { clearFirestoreCache } from "~/lib/browser-util";
import convergeLogo from "~/public/static/img/converge_logo_final.svg";
import { marketingNavItemsRight, newsFeedRoute } from "~/routes";
import CreateAccount from "./auth/CreateAccount";
import SignIn from "./auth/SignIn";
import { LoggedInNav, LoggedOutNav, MarketingNav } from "./NavMenu";
import { useGlobalState } from "~/lib/globalHeaderState";
import useNetworkFromUrl from "~/hooks/useNetwork";
import { isEmpty } from "lodash";

export default function Header(): React.ReactElement {
  const [loginTrace, setLoginTrace] = useState<firebase.performance.Trace>();

  const auth = useAuth();
  useEffect(() => {
    // must be initialized client side only
    const perf = firebase.performance();
    // only set loginTrace once
    console.log("Header set loginTrace", { loginTrace });
    if (!loginTrace) {
      const trace = perf.trace("AUTH_USER_LOADED");

      setLoginTrace(trace);
      console.log("Header start trace", { trace });
      trace.start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const router = useRouter();
  const { status, data: signInCheckResult } = useSigninCheck();
  const { asPath } = router;
  useEffect(() => {
    const onRouteChangeStart = () => {
      clearFirestoreCache();
    };
    router.events.on("routeChangeStart", onRouteChangeStart);

    return () => {
      router.events.off("routeChangeStart", onRouteChangeStart);
    };
  }, [router.events]);
  const [createAccountVisible, setCreateAccountVisible] = useGlobalState(
    "isCreateAccountOverlayOpen"
  );
  const [signInVisible, setSignInVisible] = useGlobalState(
    "isSignInOverlayOpen"
  );
  const { data: user } = useUser();
  const { currentUser } = auth;
  const isUnverified = user && !user.emailVerified;
  console.log("Header", { isUnverified, user, currentUser });

  const network = useNetworkFromUrl();
  const hasSponsors = !isEmpty(network?.sponsors);
  // we only show network-wide sponsors on the newsfeed
  const isFeed = network && asPath == newsFeedRoute(network.slug);

  useEffect(() => {
    // user is loaded, so stop the trace
    if (user && loginTrace) {
      console.log("Header stop trace loginTrace", { loginTrace });
      loginTrace.stop();
      // prevent this block from firing again
      setLoginTrace(undefined);
    }
  }, [user, loginTrace]);

  const logoHref = "/";

  console.count("Render Header");

  // TODO - don't hardcode these
  const marketingPaths = [
    ...["/campus", "/about", "/contact", "/billings", "/billings/sponsorship"],
    ...(user
      ? []
      : ["/billings/events", "/billings/groups", "/rvu/events", "/rvu/groups"]),
  ];
  const isMarketing = marketingPaths.find((path) => asPath.startsWith(path));

  const isCampusPage = ["/campus", "/contact", "/about", "/rvu"].find((path) =>
    asPath.startsWith(path)
  );

  const networkSlug = isCampusPage ? "rvu" : "billings";
  const marketingNavItems = marketingNavItemsRight(
    networkSlug,
    isCampusPage ? "/campus" : undefined
  );

  console.log("Header isMarketing", {
    status,
    isMarketing,
    asPath,
    isCampusPage,
  });

  const [resendDisabled, setResendDisabled] = useState(false);

  const resendVerification = async () => {
    // const url = window.location.href;
    // const actionCodeSettings = {
    //   handleCodeInApp: true,
    //   url,
    //   iOS: {
    //     bundleId: "is.converge.converge",
    //   },
    // };
    await user.sendEmailVerification();
    setResendDisabled(true);
  };

  const userNavIsLoading =
    status === "loading" || (!!signInCheckResult?.signedIn && !user);

  console.log("Auth", { userNavIsLoading, user });

  const hideMainHeaderNav = isCampusPage && !user;

  const marginBottom = hideMainHeaderNav ? 4 : isMarketing ? 16 : 13;

  return (
    <>
      <Container
        maxWidth={false}
        sx={{
          mb: marginBottom,
          padding: 0,
          height: "auto",
        }}
      >
        <AppBar
          elevation={1}
          position="fixed"
          sx={{ m: 0, p: 0, background: "white" }}
        >
          {hideMainHeaderNav ? (
            <></>
          ) : (
            <Toolbar sx={{ px: 2 }}>
              <Link href={logoHref}>
                <a
                  style={{
                    textDecoration: "none",
                    fontWeight: "bold",
                    marginRight: 10,
                    marginTop: 5,
                  }}
                  title="Converge"
                >
                  <div>
                    <img
                      alt="Converge Collective"
                      src={convergeLogo}
                      height={38}
                      width={40}
                    />
                  </div>
                </a>
              </Link>
              {userNavIsLoading ? (
                <>
                  <div style={{ marginLeft: "auto" }} />
                  <Skeleton variant="circular" width={40} height={40} />
                </>
              ) : isMarketing && !user ? (
                <MarketingNav
                  showLeftNav={!isCampusPage}
                  showAuthNav={!isCampusPage}
                  networkSlug={networkSlug}
                  setSignInVisible={setSignInVisible}
                  setCreateAccountVisible={setCreateAccountVisible}
                />
              ) : user ? (
                <LoggedInNav />
              ) : (
                <LoggedOutNav
                  setSignInVisible={setSignInVisible}
                  setCreateAccountVisible={setCreateAccountVisible}
                />
              )}
            </Toolbar>
          )}
          {/*  disable the verify banner since people are having trouble verifying */}
          {isUnverified && false && (
            <Alert severity="info">
              <b>Thank you for signing up!</b> Please verify your email address.{" "}
              {resendDisabled ? (
                <>✓ Sent!</>
              ) : (
                <MuiLink
                  component="button"
                  disabled={resendDisabled}
                  onClick={resendVerification}
                >
                  Resend email.
                </MuiLink>
              )}
            </Alert>
          )}
          {!user && (
            <Alert
              icon={
                isCampusPage ? (
                  <AutoStoriesIcon fontSize="inherit" />
                ) : (
                  <HikingIcon fontSize="inherit" />
                )
              }
              severity="info"
              sx={{
                width: "100%",
                mt: 0,
                px: 4,
                py: 0.2,
                "& .MuiAlert-message": { width: "100%" },
              }}
            >
              <Stack direction="row" justifyContent="space-between">
                {!isMarketing ? (
                  <Typography variant="body2">
                    Do more of the things you love with the people that share
                    your passions in Billings.{" "}
                    <Link href="/billings">Learn more.</Link>
                  </Typography>
                ) : (
                  <Stack direction="row">
                    {marketingNavItems.map(({ url, onClick, label }) => (
                      <Box key={url} sx={{ mx: 1 }}>
                        <Typography variant="body2">
                          {url ? (
                            <Link href={url} key={url}>
                              <a
                                style={{
                                  textDecoration: "none",
                                  color: "#000",
                                }}
                              >
                                {label}
                              </a>
                            </Link>
                          ) : (
                            <Button onClick={onClick}>{label}</Button>
                          )}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Alert>
          )}
        </AppBar>

        {/* Modals: */}

        <CreateAccount
          isOpen={createAccountVisible}
          handleClose={() => setCreateAccountVisible(false)}
        />

        <SignIn
          isOpen={signInVisible}
          handleClose={() => setSignInVisible(false)}
        />
      </Container>

      <Container disableGutters={true} maxWidth="lg" sx={{ mb: 3 }}>
        {network && isFeed && hasSponsors ? (
          <Alert
            variant="outlined"
            severity="info"
            icon={<BusinessIcon fontSize="inherit" />}
          >
            <span>{network.name} Network powered by: </span>
            {network.sponsors?.map((sponsor) => (
              <span key={sponsor.name}>
                <a href={sponsor.url} target="_blank" rel="noreferrer">
                  {sponsor.name}
                </a>{" "}
              </span>
            ))}
          </Alert>
        ) : null}
      </Container>
    </>
  );
}
